// Pure, DOM-free helpers for profile state presentation and error mapping.
// Kept side-effect free so they can be unit-tested without a DOM harness.
import type { Profile } from '../api/types.js';

export interface ProfileStateView {
  enabled: boolean;
  disabled: boolean;
  isPublic: boolean;
  deletable: boolean;        // public profiles can never be deleted
  statusLabel: string;       // 'Active' | 'Disabled'
  statusBadgeClass: string;  // css badge class
  toggleLabel: string;       // action verb for the toggle button
}

// enabled is optional in the API type; default TRUE (active) when absent so
// older payloads render as active rather than accidentally "disabled".
export function profileStateView(p: Profile): ProfileStateView {
  const enabled = p.enabled !== false;
  const isPublic = p.is_public === true;
  return {
    enabled,
    disabled: !enabled,
    isPublic,
    deletable: !isPublic,
    statusLabel: enabled ? 'Active' : 'Disabled',
    statusBadgeClass: enabled ? 'badge badge-ok' : 'badge badge-warn',
    toggleLabel: enabled ? 'Disable' : 'Enable',
  };
}

// Detect the subscriber-facing "profile temporarily disabled" signal, from
// either an HTTP error code/message or a contact sync_state annotation.
export function isProfileDisabledError(input: { code?: string; message?: string; sync_state?: string }): boolean {
  return input.code === 'profile_disabled'
    || input.sync_state === 'profile_disabled'
    || /temporarily disabled/i.test(input.message || '');
}

// Detect a public not-found (missing OR disabled — uniform, no distinction).
export function isPublicNotFound(status: number): boolean {
  return status === 404;
}
